// @ts-check
// Rotas REST de automações e notificações (docs/arquitetura-appcrm/08-contrato-automacoes.md, seção 5).
import { api } from './api-client.js';

/**
 * @typedef {{ id?: string, type: string, [key: string]: any }} AutomationAction
 * @typedef {{ id: string, status: string, createdAt: string, endedAt: string | null, error: string | null }} AutomationRunSummary
 * @typedef {{ objectNameSingular: string, triggerType: string, triggerSettings: Record<string, any>, conditions: any, actions: AutomationAction[] }} AutomationDefinition
 * @typedef {{ id: string, number: number, status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED', createdAt: string, updatedAt: string, publishedAt: string | null,
 *   createdByWorkspaceMemberId: string | null, definition: AutomationDefinition }} AutomationVersion
 * @typedef {{ id: string, receivedAt: string, status: string, httpStatus: number, runId: string | null, error: string | null, bodyBytes: number }} WebhookDelivery
 * @typedef {{ id: string, name: string, description: string | null, objectNameSingular: string,
 *   triggerType: string, triggerSettings: Record<string, any>, conditions: any, actions: AutomationAction[], isEnabled: boolean, packageKey: string | null,
 *   isPackage: boolean, createdAt: string, updatedAt: string, runCount: number, recentFailureCount: number, lastRun: AutomationRunSummary | null,
 *   versionId?: string | null, versionNumber?: number | null, draft?: { id: string, number: number, updatedAt: string, definition: AutomationDefinition } | null,
 *   webhook?: { path: string, authentication: string, secret: string | null } | null, nextRunAt?: string | null }} Automation
 * @typedef {{ index: number, actionId: string, type: string, status: string, error?: string | null, output?: any }} AutomationRunStep
 * @typedef {{ id: string, automationId: string, automationName: string | null, objectNameSingular: string, recordId: string | null,
 *   recordLabel: string | null, triggerType: string, status: string, steps: AutomationRunStep[], error: string | null, attempts: number,
 *   createdAt: string, startedAt: string | null, endedAt: string | null, resumeAt?: string | null, triggeredBy?: string | null, versionNumber?: number | null }} AutomationRun
 * @typedef {{ id: string, name: string, description: string | null, objectNameSingular: string, actionTypes: string[], maximumRecords: number }} ManualAutomation
 * @typedef {{ record: { id: string, label: string } | null, contact: { id: string, label: string, phone: string | null } | null,
 *   template: { id: string, name: string, category: string | null, status: string, isSendable: boolean } | null, text: string | null,
 *   variables: Record<string, string>, headerVariables: Record<string, string>, missingVariables: string[], marketingBlock: string | null }} TemplateActionPreview
 * @typedef {{ id: string, type: string, title: string, body: string | null, params: Record<string, any>, targetObjectNameSingular: string | null,
 *   targetRecordId: string | null, linkPath: string | null, createdAt: string, readAt: string | null, isRead: boolean }} AppNotification
 * @typedef {{ type: string, inAppEnabled: boolean, emailEnabled: boolean }} NotificationPreference
 * @typedef {{ taskDueSoonHours: number, unansweredMessageHours: number }} NotificationSettings
 */

/** @returns {Promise<Automation[]>} */
export async function listAutomations() {
  const body = await api.get('/rest/automations');
  return body?.data?.automations ?? [];
}

/** @param {string} automationId @returns {Promise<Automation>} */
export async function getAutomation(automationId) {
  const body = await api.get(`/rest/automations/${automationId}`);
  return body.data.automation;
}

/** @param {Partial<Automation>} input @returns {Promise<Automation>} */
export async function createAutomation(input) {
  const body = await api.post('/rest/automations', input);
  return body.data.automation;
}

/** @param {string} automationId @param {Partial<Automation>} patch @returns {Promise<Automation>} */
export async function updateAutomation(automationId, patch) {
  const body = await api.patch(`/rest/automations/${automationId}`, patch);
  return body.data.automation;
}

/** @param {string} automationId */
export async function deleteAutomation(automationId) {
  await api.delete(`/rest/automations/${automationId}`);
}

/** Publica o rascunho (contrato 08, seção 3.14). @param {string} automationId @returns {Promise<Automation>} */
export async function publishAutomation(automationId) {
  const body = await api.post(`/rest/automations/${automationId}/publish`);
  return body.data.automation;
}

/** @param {string} automationId @returns {Promise<Automation>} */
export async function discardAutomationDraft(automationId) {
  const body = await api.delete(`/rest/automations/${automationId}/draft`);
  return body.data.automation;
}

/** @param {string} automationId @returns {Promise<AutomationVersion[]>} */
export async function listAutomationVersions(automationId) {
  const body = await api.get(`/rest/automations/${automationId}/versions`);
  return body?.data?.versions ?? [];
}

/**
 * "Voltar a esta versão": vira rascunho numa automação ativa e vale na hora numa desativada.
 * @param {string} automationId @param {string} versionId @returns {Promise<{ automation: Automation, restoredAs: 'DRAFT' | 'PUBLISHED' }>}
 */
export async function restoreAutomationVersion(automationId, versionId) {
  const body = await api.post(`/rest/automations/${automationId}/versions/${versionId}/restore`);
  return body.data;
}

/** Novo segredo do webhook de entrada. @param {string} automationId @returns {Promise<Automation>} */
export async function regenerateAutomationWebhookSecret(automationId) {
  const body = await api.post(`/rest/automations/${automationId}/webhook-secret`);
  return body.data.automation;
}

/** @param {string} automationId @returns {Promise<WebhookDelivery[]>} */
export async function listAutomationWebhookDeliveries(automationId) {
  const body = await api.get(`/rest/automations/${automationId}/webhook-deliveries`);
  return body?.data?.deliveries ?? [];
}

/**
 * @param {{ automationId?: string | null, status?: string | null, limit?: number, startingAfter?: string | null }} [query]
 * @returns {Promise<{ runs: AutomationRun[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listAutomationRuns({ automationId, status, limit = 50, startingAfter } = {}) {
  const body = await api.get('/rest/automations/runs', {
    automationId: automationId ?? undefined, status: status ?? undefined, limit, starting_after: startingAfter ?? undefined,
  });
  return { runs: body?.data?.runs ?? [], pageInfo: body?.pageInfo ?? { hasNextPage: false, endCursor: null } };
}

/** @param {string} runId */
export async function retryAutomationRun(runId) {
  const body = await api.post(`/rest/automations/runs/${runId}/retry`);
  return body.data.run;
}

/** Para uma execução na fila, rodando ou em espera. @param {string} runId */
export async function stopAutomationRun(runId) {
  const body = await api.post(`/rest/automations/runs/${runId}/stop`);
  return body.data.run;
}

/**
 * Automações manuais que o membro logado pode disparar (menu de comandos e barra de seleção).
 * @param {string | null} [objectNameSingular] @returns {Promise<ManualAutomation[]>}
 */
export async function listManualAutomations(objectNameSingular = null) {
  const body = await api.get('/rest/automations/manual', { objectNameSingular: objectNameSingular ?? undefined });
  return body?.data?.automations ?? [];
}

/**
 * @param {string} automationId @param {string[]} recordIds
 * @returns {Promise<{ runs: Array<{ recordId: string, runId: string }>, skipped: Array<{ recordId: string, reason: string }> }>}
 */
export async function runManualAutomation(automationId, recordIds) {
  const body = await api.post(`/rest/automations/${automationId}/run`, { recordIds });
  return body.data;
}

/**
 * Prévia da ação de modelo do WhatsApp com um registro de verdade (o mais recente do objeto, sem recordId).
 * @param {{ objectNameSingular: string, recordId?: string | null, action: AutomationAction }} input @returns {Promise<TemplateActionPreview>}
 */
export async function previewAutomationAction(input) {
  const body = await api.post('/rest/automations/preview', input);
  return body.data.preview;
}

/**
 * @param {{ unreadOnly?: boolean, limit?: number, startingAfter?: string | null }} [query]
 * @returns {Promise<{ notifications: AppNotification[], unreadCount: number, pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listNotifications({ unreadOnly = false, limit = 20, startingAfter } = {}) {
  const body = await api.get('/rest/notifications', { unreadOnly: unreadOnly ? 'true' : undefined, limit, starting_after: startingAfter ?? undefined });
  return { notifications: body?.data?.notifications ?? [], unreadCount: body?.unreadCount ?? 0, pageInfo: body?.pageInfo ?? { hasNextPage: false, endCursor: null } };
}

/** @returns {Promise<number>} */
export async function countUnreadNotifications() {
  const body = await api.get('/rest/notifications/unread-count');
  return body?.data?.unreadCount ?? 0;
}

/** @param {{ ids?: string[], all?: boolean }} input @returns {Promise<number>} */
export async function markNotificationsRead(input) {
  const body = await api.post('/rest/notifications/mark-read', input);
  return body?.data?.unreadCount ?? 0;
}

/** @returns {Promise<{ preferences: NotificationPreference[], settings: NotificationSettings }>} */
export async function getNotificationPreferences() {
  const body = await api.get('/rest/notifications/preferences');
  return body.data;
}

/**
 * @param {{ preferences?: NotificationPreference[], settings?: Partial<NotificationSettings> }} input
 * @returns {Promise<{ preferences: NotificationPreference[], settings: NotificationSettings }>}
 */
export async function updateNotificationPreferences(input) {
  const body = await api.put('/rest/notifications/preferences', input);
  return body.data;
}
